import clsx from "clsx";
import React from "react";
import { ParsedMetric, Value } from "./metrics.types";

type FormulaDisplayProps = {
    metric: ParsedMetric;
    onValueClick?: (value: Value, index: number) => void;
    selectedRef?: string;
};

export const FormulaDisplay: React.FC<FormulaDisplayProps> = ({
    metric,
    onValueClick,
    selectedRef,
}) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    const regex = /\$(\d+)/g;
    let match: RegExpExecArray | null;

    const formatText = (text: string) => {
        const textParts: React.ReactNode[] = [];
        let textIdx = 0;
        for (let i = 0; i < text.length; i++) {
            if (text[i] === "(") {
                if (i > textIdx) {
                    textParts.push(text.slice(textIdx, i));
                }
                textParts.push(
                    <span key={`open-${parts.length}-${i}`} className="mr-1">
                        (
                    </span>,
                );
                textIdx = i + 1;
            } else if (text[i] === ")") {
                if (i > textIdx) {
                    textParts.push(text.slice(textIdx, i));
                }
                textParts.push(
                    <span key={`close-${parts.length}-${i}`} className="ml-1">
                        )
                    </span>,
                );
                textIdx = i + 1;
            }
        }
        if (textIdx < text.length) {
            textParts.push(text.slice(textIdx));
        }
        return textParts;
    };

    while ((match = regex.exec(metric.formula)) !== null) {
        const beforeMatch = metric.formula.slice(lastIndex, match.index);
        if (beforeMatch) {
            parts.push(...formatText(beforeMatch));
        }

        const valueIndex = parseInt(match[1], 10);
        const value = metric.values[valueIndex];

        if (value && value.meta) {
            parts.push(
                <a
                    key={`value-${valueIndex}`}
                    className={clsx(
                        "text-default px-2 py-1",
                        "border-default rounded-lg border",
                        "quiet",
                        "hover:bg-selected text-subdued",
                        selectedRef === value.ref && "bg-selected",
                    )}
                    style={{ color: "white" }}
                    onClick={() => onValueClick?.(value, valueIndex)}
                >
                    {value.value}
                </a>,
            );
        } else if (value) {
            parts.push(value.value.toString());
        } else {
            parts.push(match[0]);
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < metric.formula.length) {
        parts.push(...formatText(metric.formula.slice(lastIndex)));
    }

    parts.push(" = ");

    return (
        <div className="flex flex-col gap-2">
            <span className="formula-display leading-loose">{parts}</span>
            <strong key="result" className="ml-1">
                {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(metric.result)}
            </strong>
        </div>
    );
};
